import { Container, Sprite, Texture, TextureSource, Rectangle } from 'pixi.js';
import type { AssetManager } from '../render/AssetManager';

interface Burst {
  sprite: Sprite;
  frame: number;
  time: number;
}

const FRAME_W = 32;
const FRAME_H = 16;
const FPS = 22;

/**
 * One-shot air-jump burst, ported from yknytt's DoubleJump.cs: a 7-frame
 * non-looping animation (double_jump.png rows 1-7, skipping row 0) at 22fps,
 * alpha 0.549, centered on Juni at the moment of the jump.
 */
export class DoubleJumpEffect {
  readonly container = new Container();
  private frames: Texture[] = [];

  constructor() {
    this.container.zIndex = 8; // same layer as Juni
  }

  async load(assets: AssetManager): Promise<void> {
    const tex = await assets.getTexture('juni/double_jump.png');
    if (!tex) return;
    const src = tex.source as TextureSource;
    const rows = Math.max(1, Math.floor(src.height / FRAME_H));
    for (let r = 1; r < rows; r++) {
      this.frames.push(
        new Texture({ source: src, frame: new Rectangle(0, r * FRAME_H, FRAME_W, FRAME_H) }),
      );
    }
  }

  private bursts: Burst[] = [];

  /** Play the burst at a world position. */
  play(x: number, y: number): void {
    if (this.frames.length === 0) return;
    const sprite = new Sprite(this.frames[0]);
    sprite.anchor.set(0.5, 0.5);
    sprite.alpha = 0.549;
    sprite.x = x;
    sprite.y = y;
    this.container.addChild(sprite);
    this.bursts.push({ sprite, frame: 0, time: 0 });
  }

  update(dt: number): void {
    if (this.bursts.length === 0) return;
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const b = this.bursts[i];
      b.time += dt;
      const frame = Math.floor(b.time * FPS);
      if (frame >= this.frames.length) {
        b.sprite.destroy();
        this.bursts.splice(i, 1);
        continue;
      }
      if (frame !== b.frame) {
        b.frame = frame;
        b.sprite.texture = this.frames[frame];
      }
    }
  }

  clear(): void {
    for (const b of this.bursts) b.sprite.destroy();
    this.bursts = [];
  }

  destroy(): void {
    this.clear();
    this.container.destroy({ children: true });
  }
}
